{
  "pages": [
    {
      "guideType": "learn",
      "slug": "agentic-workflows",
      "title": "Meet Gab AI Work",
      "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact.",
      "navIcon": "briefcase",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 700,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I selected Work and sent a small, specific task.",
          "I opened the delivered artifact.",
          "I requested a focused revision in the same task."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "From an idea to a deliverable",
            "copy": "Work is Gab AI’s workspace for tasks that take more than one answer. It can research, write and run code, create files, and use media tools as it works toward your brief. You stay in the same Gab AI conversation to answer questions, review progress, and ask for changes. There is no separate coding agent to install for these lessons."
          },
          {
            "type": "section",
            "title": "1. Start a Work task",
            "copy": "Sign in to Gab AI, open New chat and select Work at the top of the page. Describe the outcome, attach any references, and name the artifact you want. Review the available Work options and your credits, then press Send. These lessons are free to read; running Work and generating media uses credits."
          },
          {
            "type": "work-prompt",
            "title": "Your first small task",
            "code": "Create an editable one-page project brief for a fictional browser game called Star Collector. Include the goal, audience, controls, three must-have features, and a checklist for testing. Keep the scope small and deliver the document as an artifact."
          },
          {
            "type": "section",
            "title": "2. Follow progress and answer questions",
            "copy": "Read Work’s progress updates to see what it is doing. When it asks for missing details, answer in the task. A plan or progress message is not the finished output. Wait for the delivered file, or read the explanation if Work cannot finish."
          },
          {
            "type": "section",
            "title": "3. Open the artifact",
            "copy": "An artifact is a result you can open: a document, spreadsheet, deck, image, code file, or video project. Open the file card in the conversation to preview or edit it. The editor and available actions depend on the file type and device; these walkthroughs use Gab AI on the web."
          },
          {
            "type": "section",
            "title": "4. Review, then revise",
            "copy": "Compare the result with your brief. Make a direct edit where supported or request a focused change in the same task. Name the artifact and what must stay the same. Download or export the final version and inspect the file you will actually use."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "I selected Work and sent a small, specific task.",
              "I opened the delivered artifact.",
              "I requested a focused revision in the same task."
            ]
          }
        ],
        "title": "Meet Gab AI Work | Gab AI Learn",
        "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, meet Gab AI Work"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/agentic-workflows",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "meet Gab AI Work"
      ],
      "navigation": {
        "next": {
          "label": "Your artifact toolkit",
          "href": "/docs/learn/agent-tools"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "agent-tools",
      "title": "Your artifact toolkit",
      "description": "Know which artifact to request, what you can edit, and how to take your work with you.",
      "navIcon": "layer-group",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 701,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I can name the artifact and export format for my project.",
          "I know when Add to chat still requires Send.",
          "I understand that a revised project needs a fresh export."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Choose the result before choosing the tool",
            "copy": "Tell Work what you want to make. It selects tools while you describe the result and review it. Ask for the editable artifact as well as any final download you need. An answer pasted into chat, an editable project, and an exported file serve different purposes."
          },
          {
            "type": "unordered-list",
            "title": "What each artifact is for",
            "items": [
              "Document: edit text, headings and tables, use AI rewrite, then export Word or PDF.",
              "Spreadsheet: edit cells, formulas, formatting and sheets, then export Excel.",
              "Presentation: edit slides and speaker notes, rehearse with Present, then export PowerPoint or PDF.",
              "Image: inspect versions, select an area, request edits or reframing, then download the chosen image.",
              "Code and HTML: run supported HTML in Preview, inspect Code (or Source for Markdown) and available Changes, and point to revisions with Select or Highlight. Source is a viewer; ask Work for code changes.",
              "Video project: review reference media and clips, compare takes, and create a final video export."
            ]
          },
          {
            "type": "section",
            "title": "1. Open the delivered file",
            "copy": "Choose the artifact card in the Work conversation. A supported HTML file opens a preview, while other code types show source. A PDF is a finished document preview; ask for an editable document when you need to change its content inside Gab AI."
          },
          {
            "type": "section",
            "title": "2. Use the right kind of edit",
            "copy": "Text, cells and slides support direct editing in their respective editors. In the image studio and HTML preview, Add to chat prepares an AI revision: you still review and send it in chat. Saving a direct edit and asking Work to generate a new result are different actions."
          },
          {
            "type": "section",
            "title": "3. Keep the deliverable current",
            "copy": "Wait for saves to finish, compare the revised artifact with the original, and export again after changes. For a multi-file project, ask Work for all required assets and instructions. Check the downloaded file before sending it to someone else."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "I can name the artifact and export format for my project.",
              "I know when Add to chat still requires Send.",
              "I understand that a revised project needs a fresh export."
            ]
          }
        ],
        "title": "Your artifact toolkit | Gab AI Learn",
        "description": "Know which artifact to request, what you can edit, and how to take your work with you. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, your artifact toolkit"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/agent-tools",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "your artifact toolkit"
      ],
      "navigation": {
        "previous": {
          "label": "Meet Gab AI Work",
          "href": "/docs/learn/agentic-workflows"
        },
        "next": {
          "label": "Write a useful Work brief",
          "href": "/docs/learn/briefing-agents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "briefing-agents",
      "title": "Write a useful Work brief",
      "description": "Specify the outcome, references, constraints, review points, and definition of done.",
      "navIcon": "list-check",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 702,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My brief names an output format and audience.",
          "My sources and scope are explicit.",
          "I included a review point and a concrete acceptance check."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Make success observable",
            "copy": "A good Work brief describes the result someone will use. Include the audience, exact output formats, source material, limits, and a test that proves the work is ready. State assumptions Work may make and which choices need your input."
          },
          {
            "type": "work-prompt",
            "title": "A reusable Work brief",
            "code": "Outcome: Create [deliverable] for [audience].\nContext: Use [attached files or facts]. Preserve [details].\nFormat: Deliver [editable artifact] plus [export format].\nScope: Include [must-haves]. Keep this first version to [limit].\nReview: Pause after [storyboard, outline, or prototype] so I can review it.\nDone means: Check [specific tests] and explain anything incomplete."
          },
          {
            "type": "section",
            "title": "1. Attach and explain references",
            "copy": "Upload the relevant source and identify its role: “Use this as factual source material” or “Match this visual style.” State which file is authoritative if several disagree. Work only has the context available in the task; do not assume it can see a file on your device that you have not attached."
          },
          {
            "type": "section",
            "title": "2. Choose an appropriate review point",
            "copy": "For a short document, ask for a complete first draft. For a video, approve the storyboard and references before generating every clip. A requested pause is an instruction to Work, so check that it has reached the review point before approving the next phase."
          },
          {
            "type": "section",
            "title": "3. Define checks and delivery",
            "copy": "For a game, list controls, collisions and restart. For a budget, give a known total. For a deck, ask it to inspect every slide. Request the actual files and an explanation of what was verified, rather than treating a confident completion message as proof."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "My brief names an output format and audience.",
              "My sources and scope are explicit.",
              "I included a review point and a concrete acceptance check."
            ]
          }
        ],
        "title": "Write a useful Work brief | Gab AI Learn",
        "description": "Specify the outcome, references, constraints, review points, and definition of done. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, write a useful work brief"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/briefing-agents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "write a useful work brief"
      ],
      "navigation": {
        "previous": {
          "label": "Your artifact toolkit",
          "href": "/docs/learn/agent-tools"
        },
        "next": {
          "label": "Review, revise, and finish",
          "href": "/docs/learn/running-agent-loops"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "running-agent-loops",
      "title": "Review, revise, and finish",
      "description": "Steer a Work task with focused feedback and verify the final artifact.",
      "navIcon": "arrows-rotate",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 703,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My revision identifies an observed issue and expected behavior.",
          "I tested the updated artifact instead of relying on the reply.",
          "I opened the final download and checked that it is the intended version."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Use a short feedback loop",
            "copy": "Start with a useful first version. Open the result, find the most important mismatch, and request a specific revision. Keep related changes in the same Work task so the brief, source files, and prior results stay together."
          },
          {
            "type": "section",
            "title": "1. Review against the brief",
            "copy": "Open and use the artifact. Play the game, change a spreadsheet input, read the document, or watch the full video with sound. Write down observed behavior and expected behavior. A screenshot helps with layout; reproduction steps help with broken interactions."
          },
          {
            "type": "work-prompt",
            "title": "A revision Work can act on",
            "code": "Update the existing Star Collector game artifact. On a narrow phone screen, the touch buttons cover the score. Move the controls below the game area and keep the score visible. Preserve keyboard controls, scoring, and artwork. Test a 390-pixel-wide layout and restart, then deliver the updated HTML."
          },
          {
            "type": "section",
            "title": "2. Preserve what already works",
            "copy": "Name the file, slide, scene, cell range, or selected region. Say what to change and what to keep. Use the artifact’s selection controls when available. For image and HTML requests prepared with Add to chat, inspect the drafted message and press Send."
          },
          {
            "type": "section",
            "title": "3. Recover from an incomplete task",
            "copy": "Read any failure or credit message before retrying. If the task is still running, avoid repeatedly launching the same job. You can stop Work when needed; stopping does not turn a partial result into a completed file. Resume with a specific next step, such as fixing one failed clip or finishing one export."
          },
          {
            "type": "section",
            "title": "4. Verify and take the work with you",
            "copy": "Recheck the changed area and the key behavior around it. Wait for editor saves or video export completion. Download the latest file and open it outside the preview. Keep editable artifacts for future changes and use exported files for delivery."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "My revision identifies an observed issue and expected behavior.",
              "I tested the updated artifact instead of relying on the reply.",
              "I opened the final download and checked that it is the intended version."
            ]
          }
        ],
        "title": "Review, revise, and finish | Gab AI Learn",
        "description": "Steer a Work task with focused feedback and verify the final artifact. Follow the steps in Gab AI Work. Review and export your result.",
        "keywords": "Gab AI, Work, artifacts, review, revise, and finish"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/running-agent-loops",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "review, revise, and finish"
      ],
      "navigation": {
        "previous": {
          "label": "Write a useful Work brief",
          "href": "/docs/learn/briefing-agents"
        },
        "next": {
          "label": "Build a playable video game",
          "href": "/docs/learn/work-games"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-games",
      "title": "Build a playable video game",
      "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game.",
      "navIcon": "gamepad",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 704,
      "guideSeo": {
        "readMinutes": 18,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I can move, collect stars, win or lose, and restart without reloading.",
          "The controls fit on a small screen and do not cover the game.",
          "I reviewed a revision and tested the downloaded HTML."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A playable HTML game",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 18,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Start with one complete game loop",
            "copy": "Open Gab AI, select Work, and paste the starter prompt below. Keep the first version small: movement, a goal, obstacles, and restart. Work will create the files and check the game. Answer any question about the controls or scope in the same task."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Build Star Collector, a small playable browser game in one self-contained HTML file with embedded CSS and JavaScript. Move with arrow keys or WASD and on-screen touch controls. Collect 10 stars, avoid three moving obstacles, show score and a win or lose screen, and include a working Restart button. Use a midnight-blue space theme. No external assets, logins, or network calls. Test movement, collisions, scoring, restart, and a narrow mobile layout. Deliver the HTML game as a file artifact and tell me what you tested."
          },
          {
            "type": "section",
            "title": "2. Open the code artifact and play",
            "copy": "When Work delivers the HTML file, open its file card. Preview runs the game; click inside it before using keyboard controls. Collect stars, hit an obstacle, reach the win screen, and restart. Use the preview device selector to check a phone-sized layout. Test touch controls on a real touch device too."
          },
          {
            "type": "section",
            "title": "3. Point to a change",
            "copy": "In the HTML preview, use Select to pick a page element or Highlight to mark a region. Choose Ask for changes and describe the revision. A game drawn on a canvas is usually one element: highlight the region and describe the gameplay issue in words. Add to chat prepares the request; review it and press Send in the conversation."
          },
          {
            "type": "section",
            "title": "4. Inspect the revision",
            "copy": "Open the updated artifact. The Code tab lets you read the HTML file; Changes appears when an earlier source is available. For a simple static text element, Select may offer Edit text and Save copy. Use Work for game logic and dynamic text. Replay your test sequence after every change."
          },
          {
            "type": "section",
            "title": "5. Download and test the file",
            "copy": "Download the final HTML and open it in your browser. Because this exercise embeds its assets, it should run without a server. Check the downloaded copy as well as the preview. If you later request separate assets, ask Work to package the complete folder with instructions."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In Star Collector, make the player 20% slower and enlarge the touch controls. Keep the scoring, star count, visual style, and restart behavior. Update the existing game artifact and retest keyboard movement, collisions, winning, and restart."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If the preview is blank, try Restart preview, then tell Work what you see and ask it to fix the HTML artifact. If controls do nothing, click into Preview and leave Select or Highlight mode. If only the download fails, report that separately from the preview."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I can move, collect stars, win or lose, and restart without reloading.",
              "The controls fit on a small screen and do not cover the game.",
              "I reviewed a revision and tested the downloaded HTML."
            ]
          },
          {
            "type": "section",
            "title": "Share a public site",
            "copy": "For websites, browser games, and interactive HTML, choose Publish site on the Work creation to get a public link for friends. Review the address, public access, and plan limits, then wait for confirmed publication before copying the link. Manage expiration and unpublishing in My sites. On older clients, ask Work to publish the creation in the same chat."
          },
          {
            "type": "quick-links",
            "title": "Publishing guide",
            "items": [
              {
                "label": "Publish and share your sites",
                "href": "/docs/sites"
              }
            ]
          }
        ],
        "title": "Build a playable video game | Gab AI Learn",
        "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, build a playable video game"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-games",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a playable video game"
      ],
      "navigation": {
        "previous": {
          "label": "Review, revise, and finish",
          "href": "/docs/learn/running-agent-loops"
        },
        "next": {
          "label": "Create and edit an image",
          "href": "/docs/learn/work-images"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-images",
      "title": "Create and edit an image",
      "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format.",
      "navIcon": "image",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 705,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I inspected the image at a useful zoom.",
          "I sent one focused edit from Add to chat and compared the result.",
          "I downloaded the intended version and checked its dimensions."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An image and a revised version",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Give Work an image brief",
            "copy": "Select Work and send the prompt below. Name the subject, style, shape, and where the image will be used. You can attach a reference image and explain which details to preserve. For this exercise, keep the cover free of lettering so you can judge the composition first."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create a square cover image for a browser game called Star Collector: a friendly small astronaut reaching for glowing gold stars, midnight-blue space, simple bold shapes, playful illustrated style. Leave clear space at the top for a title I will add later. Do not generate lettering. Deliver the image as an artifact."
          },
          {
            "type": "section",
            "title": "2. Open the image studio",
            "copy": "Open the delivered image artifact. The image history lets you move between available images. Use Zoom to inspect the face, edges, hands, and background before making changes. You can also upload a source image into the studio."
          },
          {
            "type": "section",
            "title": "3. Edit a specific area",
            "copy": "Choose Edit for a general change, or Comment to select an area and describe what should change there. Drag a rectangle around the part of the image you want to revise. Write one clear instruction, such as “Replace the red planet in this area with a small gold star; preserve the astronaut.”"
          },
          {
            "type": "section",
            "title": "4. Send the edit and compare",
            "copy": "Press Add to chat. This prepares your instructions and image context in the conversation; review them and press Send. Wait for the new image, then compare it with the original in the image history. Selecting a tool alone does not run an edit."
          },
          {
            "type": "section",
            "title": "5. Try another tool and download",
            "copy": "Remove targets an unwanted object; Remove BG requests a transparent PNG; Resize reframes to an aspect ratio; Variation makes a new take. Try Resize at 16:9 for a banner and ask to preserve the astronaut. Download the version you prefer from the studio."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Reframe this Star Collector cover as a 16:9 banner. Keep the astronaut and illustrated style, move the subject to the right third, and leave uncluttered dark space on the left. Do not add text."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an edit changes too much, select the original in history and request a smaller change. AI reframing can redraw details; it is not a mechanical crop. For a transparent export, inspect the downloaded PNG in a viewer that shows transparency."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I inspected the image at a useful zoom.",
              "I sent one focused edit from Add to chat and compared the result.",
              "I downloaded the intended version and checked its dimensions."
            ]
          }
        ],
        "title": "Create and edit an image | Gab AI Learn",
        "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, create and edit an image"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-images",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create and edit an image"
      ],
      "navigation": {
        "previous": {
          "label": "Build a playable video game",
          "href": "/docs/learn/work-games"
        },
        "next": {
          "label": "Make an editable document",
          "href": "/docs/learn/work-documents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-documents",
      "title": "Make an editable document",
      "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports.",
      "navIcon": "file-lines",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 706,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The facts and controls match the project.",
          "A direct edit reached the saved state.",
          "I opened the exported DOCX or PDF and checked its layout."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable guide, DOCX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Ask for the file you need",
            "copy": "Select Work and send the prompt below. Specify an editable document, audience, length, sections, and source material. If you have an existing brief, attach it and explain which facts must stay exact. This exercise uses a fictional game, so no research is needed."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable two-page player guide for a fictional browser game called Star Collector. Audience: first-time players age 10 and up. Include a title, short introduction, keyboard and touch controls, the goal of collecting 10 stars, three practical tips, and a troubleshooting table. Use clear headings and short paragraphs. Treat the game details in this brief as the source of truth. Deliver an editable document artifact, not just text in chat."
          },
          {
            "type": "section",
            "title": "2. Open and review the document",
            "copy": "Open the document artifact. Read the headings and controls before adjusting the appearance. Correct any mismatch with your game. A downloadable DOCX or PDF alone is not the same as an editable document artifact: ask Work for the editable version if you need in-app editing."
          },
          {
            "type": "section",
            "title": "3. Make a direct edit",
            "copy": "Click in the document and change the title or a sentence. Select text to use formatting controls, add a list, or insert a table. Use Find and replace for repeated wording. Watch the save status and wait until your changes are saved."
          },
          {
            "type": "section",
            "title": "4. Revise with AI",
            "copy": "Select a paragraph and use AI rewrite for a local wording change, or ask Work in the same task to revise a named section. Be explicit about the document and preserve the facts you have already checked. If the editor reports a newer version while you have local edits, review the choice before replacing your draft."
          },
          {
            "type": "section",
            "title": "5. Export and inspect",
            "copy": "Open Export and choose Word or PDF. Use Page setup to adjust print layout when needed. Open the downloaded file and inspect page breaks, headings, tables, and spacing. Keep the editable document for future revisions; download a fresh export after editing."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In the Star Collector player guide, rewrite only the introduction in a warmer voice and shorten it to 60 words. Preserve the controls, game rules, troubleshooting table, and existing headings. Update the editable document."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an export has an awkward page break, adjust Page setup or shorten that section and export again. If saving fails, keep the editor open and resolve the save error before closing. Use Version history to inspect available saved versions."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "The facts and controls match the project.",
              "A direct edit reached the saved state.",
              "I opened the exported DOCX or PDF and checked its layout."
            ]
          }
        ],
        "title": "Make an editable document | Gab AI Learn",
        "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, make an editable document"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-documents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make an editable document"
      ],
      "navigation": {
        "previous": {
          "label": "Create and edit an image",
          "href": "/docs/learn/work-images"
        },
        "next": {
          "label": "Build a working spreadsheet",
          "href": "/docs/learn/work-spreadsheets"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-spreadsheets",
      "title": "Build a working spreadsheet",
      "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook.",
      "navIcon": "table",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 707,
      "guideSeo": {
        "readMinutes": 14,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The initial grand total is 370.",
          "Changing Testing quantity to 5 updates the total to 395.",
          "The exported XLSX preserves the expected sheets and calculations."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable workbook and XLSX",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 14,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Describe inputs and calculations",
            "copy": "Use Work and send the prompt below. Name each column and the calculations that must use formulas. Request an editable workbook explicitly. The sample inputs are deliberately small so you can verify the answer yourself."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable spreadsheet artifact for a fictional Star Collector launch budget. Sheet Budget: columns Item, Quantity, Unit cost, Total. Rows: Art, 2, 75; Music, 1, 120; Testing, 4, 25. Use formulas for each row total and a grand total, currency formatting, and clearly marked input cells. Add a Summary sheet with the overall budget and a simple chart. Explain which cells I can change. Deliver an editable workbook and an XLSX export."
          },
          {
            "type": "section",
            "title": "2. Open the spreadsheet artifact",
            "copy": "Open the workbook and switch to Budget. Check headers, rows, number formats, and sheet names. Select a calculated cell and inspect its formula in the formula bar. The expected row totals are 150, 120, and 100; the starting grand total is 370."
          },
          {
            "type": "section",
            "title": "3. Test a formula by changing an input",
            "copy": "Change Testing quantity from 4 to 5. Its total should become 125 and the grand total should become 395. Check that Summary agrees. A total that does not change may be hard-coded or reference the wrong cell; ask Work to repair the formula."
          },
          {
            "type": "section",
            "title": "4. Refine the workbook",
            "copy": "Use the editor to adjust cell formatting, add a sheet, or rename a tab. Ask Work to add a contingency calculation in the same workbook. State which inputs and formulas must remain intact, then repeat the known-answer test."
          },
          {
            "type": "section",
            "title": "5. Export and reopen",
            "copy": "Wait for the saved state, then use Export for an Excel workbook. Open the XLSX in your spreadsheet application and check the formulas and chart again. CSV is useful for a flat table, but does not preserve workbook sheets, formatting, or live formulas."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Update this launch budget workbook with a clearly labeled 10% contingency based on the grand total and a total including contingency. Preserve the input rows and existing formulas. With a base total of 395, the contingency should be 39.50 and the final amount 434.50."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a formula is unsupported in the editor, ask Work for a compatible formula or a simpler calculation and verify the export separately. Never judge a spreadsheet only by how polished it looks; change an input and check the result."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "The initial grand total is 370.",
              "Changing Testing quantity to 5 updates the total to 395.",
              "The exported XLSX preserves the expected sheets and calculations."
            ]
          }
        ],
        "title": "Build a working spreadsheet | Gab AI Learn",
        "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, build a working spreadsheet"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-spreadsheets",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a working spreadsheet"
      ],
      "navigation": {
        "previous": {
          "label": "Make an editable document",
          "href": "/docs/learn/work-documents"
        },
        "next": {
          "label": "Create a presentation",
          "href": "/docs/learn/work-presentations"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-presentations",
      "title": "Create a presentation",
      "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export.",
      "navIcon": "chalkboard",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 708,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "Each slide communicates one clear idea.",
          "I edited a slide and reviewed the speaker notes.",
          "I presented the deck and inspected the exported PPTX or PDF."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable deck, PPTX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Brief Work on the story",
            "copy": "Send the starter prompt in Work. Give the audience, purpose, slide count, visual style, and facts. Attach your game cover if you want Work to use it, and identify it as the title-slide image. Ask for an editable presentation artifact."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable five-slide presentation for the fictional Star Collector browser game. Slides: title and one-sentence pitch; how to play; what makes it fun; a simple launch plan; next steps. Audience: friends trying the prototype. Use midnight blue, white, and gold, one clear idea per slide, large readable text, and short speaker notes. Do not invent user numbers or market statistics. Deliver an editable presentation artifact."
          },
          {
            "type": "section",
            "title": "2. Review the slide sequence",
            "copy": "Open the deck artifact and use the slide thumbnails to inspect every slide. Check the narrative before polishing: a viewer should understand the game, its controls, and the next step. Remove unsupported claims or ask Work to replace them with clearly labeled plans."
          },
          {
            "type": "section",
            "title": "3. Edit a slide and its notes",
            "copy": "Select a slide and edit its text in the presentation editor. Shorten a crowded title, adjust the content, and update Speaker notes. Check the save status. Keep explanations in the notes when the slide itself would become too dense."
          },
          {
            "type": "section",
            "title": "4. Rehearse and revise",
            "copy": "Use Present to walk through the deck. Read titles from a distance and check that images are not stretched or text clipped. Ask Work to revise one named slide while preserving the rest. Inspect the revised slide and any surrounding slides it affects."
          },
          {
            "type": "section",
            "title": "5. Export for your audience",
            "copy": "Use Export to download PowerPoint for further editing or PDF for fixed pages. Open the exported file and review all slides. Keep the editable artifact for future updates and re-export after changes."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Revise only slide 3 of the Star Collector presentation. Replace the long paragraph with three short points about simple controls, quick rounds, and replayability. Keep the midnight-blue and gold design and preserve the other four slides."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If text clips in an export, shorten it or request a layout adjustment. A PDF has fixed pages; use the PowerPoint export when your recipient needs to edit the slides."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "Each slide communicates one clear idea.",
              "I edited a slide and reviewed the speaker notes.",
              "I presented the deck and inspected the exported PPTX or PDF."
            ]
          }
        ],
        "title": "Create a presentation | Gab AI Learn",
        "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, create a presentation"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-presentations",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create a presentation"
      ],
      "navigation": {
        "previous": {
          "label": "Build a working spreadsheet",
          "href": "/docs/learn/work-spreadsheets"
        },
        "next": {
          "label": "Make a video, step by step",
          "href": "/docs/learn/work-videos"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-videos",
      "title": "Make a video, step by step",
      "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video.",
      "navIcon": "film",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 709,
      "guideSeo": {
        "readMinutes": 20,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I approved the storyboard and inspected the reference images.",
          "I watched every included clip and selected the intended takes.",
          "I watched the full export with sound and downloaded the correct MP4."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A reviewed video project and MP4",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 20,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Plan before generating",
            "copy": "In Work, send the starter prompt below. Review the proposed shots, duration, aspect ratio, and sound. Start with three short shots so each is easy to inspect. Ask about the expected work and available credits before approving media generation; video generations and revisions use credits and timing can vary."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Help me make a 15-second, 16:9 teaser for a fictional space game called Star Collector. Three shots: a small astronaut notices a gold star; the astronaut dodges an asteroid; a field of stars reveals the adventure ahead. Playful illustrated style, midnight blue and gold, instrumental music, no voiceover or generated lettering. First propose a three-shot storyboard and the assets needed. Wait for my approval before generating media. We will review the reference images, then clips, then export the finished video."
          },
          {
            "type": "section",
            "title": "2. Create and review reference media",
            "copy": "Once the plan is right, tell Work to generate the reference images and pause for review. Open the video project artifact. Reference media groups available assets under Scenes, Characters, Other, and Audio. Select a scene image, inspect the subject and style, and use its available redo or image-choice controls to improve it before animating."
          },
          {
            "type": "section",
            "title": "3. Generate clips from the approved look",
            "copy": "Tell Work which reference images to use and ask it to generate the clips, pausing before the final export. Image choices may be locked while clips are generating. In Clips, select each shot from the timeline and play it. Check motion, continuity, duration, and whether the intended action actually happens."
          },
          {
            "type": "section",
            "title": "4. Improve one shot",
            "copy": "Use the selected shot’s Redo control to describe a specific change. Compare available takes and choose the one you want included. Use the timeline controls to reorder or exclude clips where available. Excluding every clip leaves nothing to export. Keep a shot you like instead of regenerating the whole film."
          },
          {
            "type": "section",
            "title": "5. Export the chosen sequence",
            "copy": "After reviewing all included clips and audio, use the project’s export action. Wait for stitching to finish; editing may be locked during that job. Open Final video and play the full result with sound. Check transitions, beginning and ending, and the music level before choosing Download video."
          },
          {
            "type": "section",
            "title": "6. Make a new version",
            "copy": "For a finished video, use Make changes to describe the next revision. Exports keeps earlier finished versions available. If you change clip choices or order, use Create new export to build an updated film. The previous MP4 does not automatically change when you edit the project."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Redo only shot 2 of the Star Collector teaser. Make the asteroid pass slowly behind the astronaut, with a steady camera and the same character design and blue-and-gold palette. Preserve shots 1 and 3 and the music. Let me compare the revised clip before creating a new export."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a clip fails, retry that shot rather than the whole project. If an export is queued, wait for the active job before requesting another. If no export arrives, use the displayed retry action and report the error in the task. A downloaded clip is only one shot; download from Final video for the complete film."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I approved the storyboard and inspected the reference images.",
              "I watched every included clip and selected the intended takes.",
              "I watched the full export with sound and downloaded the correct MP4."
            ]
          }
        ],
        "title": "Make a video, step by step | Gab AI Learn",
        "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, make a video, step by step"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-videos",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make a video, step by step"
      ],
      "navigation": {
        "previous": {
          "label": "Create a presentation",
          "href": "/docs/learn/work-presentations"
        },
        "next": {
          "label": "Use the Work video studio",
          "href": "/docs/learn/work-video-editor"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-video-editor",
      "title": "Use the Work video studio",
      "description": "Approve a storyboard, improve reference images and clips, choose takes, approve the music, and export your video.",
      "navIcon": "clapperboard",
      "navSectionSlug": "module-9-work-in-depth",
      "navOrder": 801,
      "guideSeo": {
        "readMinutes": 22,
        "difficulty": "Beginner",
        "articleSection": "Work in Depth",
        "teaches": [
          "I approved the storyboard before any media was generated.",
          "I compared image and clip versions and chose the takes I wanted.",
          "I watched the final video with sound and downloaded the correct version."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A 15-second, three-shot teaser and MP4",
              "Reference images and clips you chose yourself",
              "A revised version saved alongside the original"
            ],
            "readTime": 22,
            "prerequisites": [
              {
                "label": "Start with Make a video, step by step",
                "href": "/docs/learn/work-videos"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Brief Work and ask for a storyboard",
            "copy": "Select Work and paste the starter prompt below. Asking for a storyboard first lets you check the shots, timing, and look before anything is generated. Work replies with a script and a numbered prompt for each shot, then asks for your approval."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Help me make a 15-second, 16:9 teaser for a fictional space game called Star Collector. Three shots: a small astronaut spots a glowing gold star; the astronaut dodges a tumbling asteroid; the astronaut reaches the star as a field of stars opens up ahead. Playful illustrated style, midnight blue and gold, instrumental music, no voiceover and no on-screen text. Show me a storyboard with the script and each shot prompt first, and wait for my approval before generating anything."
          },
          {
            "type": "section",
            "title": "2. Approve the script",
            "copy": "Read the shot list. When it is right, choose the Approve script option on the card. To change it, choose Change script or type what should be different, and Work sends a revised plan. Nothing is generated until you approve. Media uses credits, so Work may then show a credits card with an estimate: choose Approve to start or Don't start to stop."
          },
          {
            "type": "section",
            "title": "3. Open the Video studio",
            "copy": "As the reference images arrive, the Video studio opens beside the chat. If you close it, choose Open video studio in the chat's Sources & Outputs panel. Three steps run across the top: Reference media, Clips, and Final video. Reference media sorts your files into tabs such as Characters, Scenes, and Audio. Select an image to see it large."
          },
          {
            "type": "section",
            "title": "4. Improve a reference image",
            "copy": "Select the image you want to change and choose Improve this image. In What would you like to change?, describe the change in everyday words, or tap a suggestion such as Keep character consistent. Choose Create new version. Your original is kept. When the new take arrives, compare the takes under Versions and choose Use this image on the one you prefer."
          },
          {
            "type": "section",
            "title": "5. Approve the images and create clips",
            "copy": "When every image looks right, choose Approve images & create clips. The versions you chose become the starting frames for your clips; for any image you did not choose, the latest version is used. Work may show another credits card for the clips: choose Approve to start. Image choices are locked while the clips are made."
          },
          {
            "type": "section",
            "title": "6. Review and arrange the clips",
            "copy": "Open Clips. Select a shot in the Timeline to watch it. Reorder shots with the arrows under each clip, or drag them on the web. Choose Exclude to leave a shot out of the final video, and Include again to bring it back. At least one clip must stay included."
          },
          {
            "type": "section",
            "title": "7. Improve one shot",
            "copy": "Select a shot and choose Improve this shot. Describe the change or tap a suggestion such as Smoother camera, then choose Create new version. Compare the takes under Versions and choose the Use button for the take you want, such as Use Take 2. The take that will go into the film shows a green Final label; until you choose, the latest take is used."
          },
          {
            "type": "section",
            "title": "8. Create the final video",
            "copy": "When your clips are ready, choose Create final video. Work stitches a preview and writes music for it, then asks you to review each part. Choose Approve visuals or Change visuals. For the music, choose Approve music, Change music, No music, or Upload my own music. Finally, approve the combined preview so Work assembles the full video. Editing is locked while it stitches."
          },
          {
            "type": "section",
            "title": "9. Download and revise the final video",
            "copy": "Open Final video and watch it all the way through with sound. Every export is saved under Video versions, so you can switch between them. Choose Download video to save the one you are viewing. To revise it, type in the Make changes box or tap Shorter cut, Vertical video, or Adjust music to start from a suggestion, edit the text, and choose Create revised video. The revision becomes a new version."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Revise the finished Star Collector teaser into a 9:16 vertical version for social media. Keep the astronaut and the gold star in frame in every shot, keep the same music and shot order, and save it as a new version so the 16:9 export stays available."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If one image or clip misses, improve just that one instead of starting over. If the studio says the timeline was not saved, choose Retry before creating the final video. If every scene is excluded, include at least one clip. A downloaded clip is a single shot; download from Final video for the complete film."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I approved the storyboard before any media was generated.",
              "I compared image and clip versions and chose the takes I wanted.",
              "I watched the final video with sound and downloaded the correct version."
            ]
          }
        ],
        "title": "Use the Work video studio | Gab AI Learn",
        "description": "Approve a storyboard, improve reference images and clips, choose takes, approve the music, and export your video. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, video studio, AI video editor, storyboard, video clips, use the Work video studio"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-video-editor",
      "updatedAt": "2026-09-27T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "video studio",
        "video editor",
        "clips",
        "storyboard",
        "use the work video studio"
      ],
      "navigation": {
        "previous": {
          "label": "Make a video, step by step",
          "href": "/docs/learn/work-videos"
        },
        "next": {
          "label": "Create and edit images in Work",
          "href": "/docs/learn/work-image-editor"
        }
      },
      "publishedAt": "2026-09-27T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-image-editor",
      "title": "Create and edit images in Work",
      "description": "Generate a cover image, then use the Image studio to edit an area, remove objects, resize, and compare versions.",
      "navIcon": "wand-magic-sparkles",
      "navSectionSlug": "module-9-work-in-depth",
      "navOrder": 803,
      "guideSeo": {
        "readMinutes": 14,
        "difficulty": "Beginner",
        "articleSection": "Work in Depth",
        "teaches": [
          "I edited one area of the image and compared the result with the original.",
          "I tried at least one other tool, such as Remove, Resize, or Variation.",
          "I downloaded the version I wanted and checked its size."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A Star Collector cover image",
              "Edited versions made in the Image studio",
              "A downloaded image in the size you need"
            ],
            "readTime": 14,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Generate a starting image",
            "copy": "Select Work and send the starter prompt below. Describe the subject, style, shape, and where the image will be used. Leave lettering out for now; it is easier to judge the picture first. Work delivers the image in the chat."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create a square cover image for a browser game called Star Collector: a friendly small astronaut floating toward a glowing gold star, midnight-blue space with a few distant planets, bold simple shapes, playful illustrated style. Leave empty space at the top for a title. No text or lettering. Deliver the image as an artifact."
          },
          {
            "type": "section",
            "title": "2. Open the Image studio",
            "copy": "Select the image in the chat, or choose Open image studio in the chat's Sources & Outputs panel. The Image studio shows your picture in the middle, the tools across the top, and the task image history down the side. Use Zoom below the picture to check details before you change anything. Upload adds your own image to work on."
          },
          {
            "type": "section",
            "title": "3. Describe an edit",
            "copy": "Choose Edit and describe the change in the instructions box, for example “Make the gold star larger and brighter.” To limit the change to one part of the picture, drag a rectangle over it first. Choose Comment when you want to mark an area and say what should change there. Clear selection removes the rectangle."
          },
          {
            "type": "section",
            "title": "4. Send it through chat",
            "copy": "Choose Add to chat. This puts your request and the image in the chat box; nothing runs until you review it and press Send. When the new image arrives, it joins the image history. Select earlier images there to compare them. Your original is kept."
          },
          {
            "type": "section",
            "title": "5. Try the other tools",
            "copy": "Remove: drag around an object, such as a distant planet, and AI fills in the space. Remove BG keeps the subject and returns a transparent PNG. Resize reframes the picture to a new shape, such as 16:9, without stretching the subject. Variation makes a fresh take in the same style. New image starts from a description alone. Each tool sends its request through Add to chat."
          },
          {
            "type": "section",
            "title": "6. Download the version you want",
            "copy": "Select the version you prefer in the image history and use the download button at the top of the studio. Check the size shown below the picture. Images Work creates are also saved to your Files library, so you can find them later."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Make a 16:9 banner version of the Star Collector cover. Keep the astronaut, the gold star, and the illustrated style. Place the astronaut on the right third and leave calm, dark space on the left for a title. No text."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an edit changes too much, select the original in the image history, draw a smaller area, and describe a smaller change. Resize can redraw parts of the picture rather than cropping it, so check faces and edges. If an image will not load, choose Retry loading image or pick another image from the history."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I edited one area of the image and compared the result with the original.",
              "I tried at least one other tool, such as Remove, Resize, or Variation.",
              "I downloaded the version I wanted and checked its size."
            ]
          }
        ],
        "title": "Create and edit images in Work | Gab AI Learn",
        "description": "Generate a cover image, then use the Image studio to edit an area, remove objects, resize, and compare versions. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, Image studio, AI image editor, remove background, resize image, create and edit images in Work"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/learn/work-image-editor",
      "updatedAt": "2026-09-27T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "image studio",
        "image editor",
        "remove background",
        "resize",
        "create and edit images in work"
      ],
      "navigation": {
        "previous": {
          "label": "Use the Work video studio",
          "href": "/docs/learn/work-video-editor"
        }
      },
      "publishedAt": "2026-09-27T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work",
      "title": "Work overview",
      "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact.",
      "navIcon": "briefcase",
      "navSectionSlug": "work-agent",
      "navOrder": 20,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I selected Work and sent a small, specific task.",
          "I opened the delivered artifact.",
          "I requested a focused revision in the same task."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "From an idea to a deliverable",
            "copy": "Work is Gab AI’s workspace for tasks that take more than one answer. It can research, write and run code, create files, and use media tools as it works toward your brief. You stay in the same Gab AI conversation to answer questions, review progress, and ask for changes. There is no separate coding agent to install for these lessons."
          },
          {
            "type": "section",
            "title": "How many Work sessions can I run at once?",
            "copy": "Work requires Plus. Plus includes up to 3 active Work sessions at once across your account; Plus Premium includes up to 10. Starting or resuming a session uses a slot. Sessions that are starting or running count, including a running session waiting for your answer. Finished, stopped, and idle sessions do not count. At the limit, wait for one to finish or stop a session and try again. You can still send follow-ups to an already-running session. Plus members who need more sessions can explore Plus Premium. Credits and the separate hourly limit on new Work tasks still apply."
          },
          {
            "type": "section",
            "title": "1. Start a Work task",
            "copy": "Sign in to Gab AI, open New chat and select Work at the top of the page. Describe the outcome, attach any references, and name the artifact you want. Review the available Work options and your credits, then press Send. These lessons are free to read; running Work and generating media uses credits."
          },
          {
            "type": "work-prompt",
            "title": "Your first small task",
            "code": "Create an editable one-page project brief for a fictional browser game called Star Collector. Include the goal, audience, controls, three must-have features, and a checklist for testing. Keep the scope small and deliver the document as an artifact."
          },
          {
            "type": "section",
            "title": "2. Follow progress and answer questions",
            "copy": "Read Work’s progress updates to see what it is doing. When it asks for missing details, answer in the task. A plan or progress message is not the finished output. Wait for the delivered file, or read the explanation if Work cannot finish."
          },
          {
            "type": "section",
            "title": "3. Open the artifact",
            "copy": "An artifact is a result you can open: a document, spreadsheet, deck, image, code file, or video project. Open the file card in the conversation to preview or edit it. The editor and available actions depend on the file type and device; these walkthroughs use Gab AI on the web."
          },
          {
            "type": "section",
            "title": "4. Review, then revise",
            "copy": "Compare the result with your brief. Make a direct edit where supported or request a focused change in the same task. Name the artifact and what must stay the same. Download or export the final version and inspect the file you will actually use."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/agentic-workflows"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          },
          {
            "type": "section",
            "title": "Work with computer folders and continue on mobile",
            "copy": "Windows and compatible direct-download Mac apps can connect Work to local project folders. On Windows, open Projects → New project and choose a source folder. On Mac, choose Project folder before sending a new Work task. Review the platform’s read-only or edit permissions and file history controls.\n\nWith Remote, pair iOS or Android with your Windows PC or Mac to start or continue shared project chats from your phone. Keep Gab AI open and the host computer awake and online while Work needs local files. Files the agent reads are sent to Gab AI. The Mac App Store edition uses cloud Work and sandboxed imports instead of local project execution or Remote hosting."
          },
          {
            "type": "quick-links",
            "title": "App setup and Remote guides",
            "items": [
              {
                "label": "Get started with the Windows app",
                "href": "/docs/windows-app"
              },
              {
                "label": "Get started with the Mac app",
                "href": "/docs/mac-app"
              },
              {
                "label": "Project folders and permissions",
                "href": "/docs/project-folders"
              },
              {
                "label": "Set up Remote on iOS or Android",
                "href": "/docs/remote"
              }
            ]
          },
          {
            "type": "section",
            "title": "Choose intelligence and review progress",
            "copy": "Fast, Standard and High intelligence balance cost and depth. You can choose a main model for text work; media tools may need a different model. Check the composer estimate and available models before sending.\n\nFollow progress, answer questions, queue a follow-up, use Send Now where offered, or stop a run. Ask first, Smart approval and Hands-off control action review; you still choose which folders, websites, apps and accounts Work may access. Review the delivered files and any failed checks before using the result."
          },
          {
            "type": "section",
            "title": "Plugins and connected accounts on Mac",
            "copy": "On compatible Mac builds, open Settings → Plugins to install reusable instructions for research, writing, meeting notes or spreadsheet review, browser workflows, and supported connected accounts. Plugins are available in Mac Settings; web, iOS and Android Settings do not offer this section. Installing a plugin does not grant access to your files or accounts.\n\nConnected packages include Notion Workspace, Gmail, Google Drive, Google Calendar, GitHub, GitLab and Slack where the provider is enabled and your account is eligible. Install the package, choose Connect, complete authorization in the browser, and return to check its connection before choosing Use in Work. The details dialog shows provider requirements. Available actions depend on the service and permissions you approved.\n\nA package labeled (Browser) uses your approved Mac browser instead; Proton Mail uses this browser workflow. You can disconnect an account or disable/remove a plugin in Settings. This stops subsequent access; it cannot undo an action already accepted by a service. New tasks use the selected plugin instructions, while existing tasks keep their original instruction snapshot."
          },
          {
            "type": "section",
            "title": "Scheduled work and availability",
            "copy": "Automated Tasks can schedule supported Work jobs with email delivery where available. They do not inherit an interactive desktop browser, command or Remote grant. Account/collection memory settings, collection instructions and relevant files can personalize Work separately from device or service access.\n\nIf Work cannot start, check your plan, credits, active-session limit and the exact connection or permission message. After an interruption, reopen the same task and check its state before starting again. For a persistent error, contact ai@gab.com with the app version and error text, without passwords or tokens."
          },
          {
            "type": "section",
            "title": "Share a public site",
            "copy": "For websites, browser games, and interactive HTML, choose Publish site on the Work creation to get a public link for friends. Review the address, public access, and plan limits, then wait for confirmed publication before copying the link. Manage expiration and unpublishing in My sites. On older clients, ask Work to publish the creation in the same chat."
          },
          {
            "type": "section",
            "title": "Custom instructions and agent rules",
            "copy": "Open Settings → Personalization → Custom instructions to save separate Chat instructions and Work agent instructions. Use Work agent instructions for recurring rules such as keeping code lines under 90 characters or providing test commands for you to run manually. Work receives its rules when a task starts and on your next follow-up or resume, independently of automatic Memory learning. Saving does not interrupt a running task. Current conversation instructions take priority. See /docs/custom-instructions for the full guide."
          },
          {
            "type": "quick-links",
            "title": "Publishing guide",
            "items": [
              {
                "label": "Publish and share your sites",
                "href": "/docs/sites"
              }
            ]
          }
        ],
        "title": "Work overview | Gab AI Docs",
        "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, work overview"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work",
      "updatedAt": "2026-10-07T16:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "work overview",
        "agent rules",
        "work rules",
        "custom instructions",
        "user defined rules"
      ],
      "navigation": {
        "next": {
          "label": "Writing a good Work task",
          "href": "/docs/work-prompts"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z",
      "supportNotes": [
        "Chat is for conversation, answers and individual generation requests. Work is for carrying a brief through research, tools, code and files to a deliverable, with follow-ups in the same conversation. Open New chat and select Work at the top of the page, describe the outcome and attach sources before sending. Bots supply reusable roles/instructions and can be used in Chat or given a job in Work. Explaining what Work can do is a support answer; it does not require starting a paid task.",
        "To continue an ended Work run, reopen its original conversation and send a follow-up in the composer (often labeled “Ask for changes…”). A completed run does not require a new conversation. Check the current task state and retry tools after resume; an earlier `session_not_running` result describes that earlier run. Preserve the original workspace, history and recorded reviews. A new task has separate context and approvals: if one is necessary, identify and attach the actual recovery files, or find saved deliverables with `list_files source=all`. Never promise that a copied resume sentence transfers the prior task's files or approval records.",
        "Work can research the web with sources, compare and summarize uploaded material, analyze data, write and run code, build websites and playable HTML games, and create or revise documents, spreadsheets, presentations, images, audio, music, voiceovers, videos and 3D assets using available tools/models. Ask for the editable artifact and required export, or a complete multi-file package with assets and instructions. Creating a website does not automatically publish it or register a domain.",
        "Work runs in a cloud workspace by default on supported web, Windows, Mac, iOS and Android clients. A cloud run can continue when the client closes. Windows and compatible direct-download Mac apps can connect permitted local project folders; their file, terminal, command and recovery controls differ. Compatible direct-download Mac builds additionally offer browser/app control with separate permissions. A cloud workspace does not automatically see the user’s computer or browser sign-ins. Remote on iOS/Android accesses shared Windows or Mac project tasks while the host stays awake, online and running Gab AI. The Mac App Store edition does not provide local project execution or Remote hosting.",
        "Work requires Plus or Plus Premium and consumes credits for model/tool/media use. Plus allows 3 active Work sessions across the account; Plus Premium allows 10. Starting and running sessions count, including a running task waiting for an answer; finished, stopped and idle sessions do not. Starting or resuming needs a slot; follow-ups to an already-running task still work at the limit. Credits and the separate hourly cap on new tasks also apply. Check the current composer estimate, model picker and plan page rather than promising a fixed cost or unlimited execution.",
        "Fast, Standard and High intelligence balance cost and depth. A manually selected main model takes priority for text work; media and capabilities that need another tool still use appropriate available models. Do not invent a model name, price, context limit or claim every model/tool is available on every account. Extra media generation and revisions can spend additional credits.",
        "Users can follow progress, answer questions, send revisions, queue a follow-up or use Send Now where offered, and stop a run. A plan, started command or progress message is not proof of completion. Review the delivered artifact and any reported failed checks. Ask first, Smart approval and Hands-off are action-review choices where available; they do not grant missing folder, website, OS, plugin or account permissions. A run can ask for approval or missing information and cannot promise to finish unattended.",
        "Work can use built-in skills and Bots and, on compatible Mac builds, installed plugins. Settings → Plugins is in the Mac app, not web/iOS/Android Settings. Portable instruction plugins include research, meeting notes, spreadsheet review and writing; browser plugins use approved Mac websites and existing sign-ins. Installing a plugin alone does not grant file/browser access or connect a service account. New tasks use their selected plugin instructions; existing tasks retain their instruction snapshot.",
        "Connected Mac plugins include Notion Workspace, Gmail, Google Drive, Google Calendar, GitHub, GitLab and Slack where the provider is configured and the account is eligible. Install the connected package → Connect → complete the provider’s browser authorization → return and check status → Use in Work. Available operations depend on the service’s exposed tools and granted scopes. A separate (Browser) package uses the Mac browser instead; Proton Mail remains a browser workflow. Provider setup/admin/preview requirements can make a listed integration unavailable. Do not claim installation proves a live connection, supports every service action, or adds separate Google Docs/Sheets/Slides connections.",
        "Plugin accounts can be disconnected and plugins disabled/removed from Mac Settings. These prevent subsequent access but cannot undo a request already accepted by the provider. Imported compatible skill packages and reviewed hosted MCP connections are supported on compatible Mac builds; arbitrary MCP servers, local executable processes and every third-party plugin format are not supported. Never ask users to paste account passwords, OAuth codes, API tokens or private keys into chat.",
        "Automated Tasks can schedule supported Work jobs with email delivery where available. Scheduled/cloud tasks do not inherit an interactive desktop browser, local command or Remote grant. Keep scheduled-task setup distinct from a running Work conversation, and do not promise that an idle chat monitors something forever. Memory, collection instructions and relevant attached files can personalize Work according to account/collection settings; they do not grant access to a whole device or connected service.",
        "For trouble starting Work, check the plan, credit balance, active-session limit, exact service error and required connection/permission. If service is unavailable, report that accurately without promising a return time. Reopen the same task after an interruption and inspect its state before duplicating paid work. For issues outside the guides, contact ai@gab.com with the relevant app version and error, without secrets.",
        "Settings → Personalization → Custom instructions has separate Chat instructions and Work agent instructions boxes (3,000 characters each). Existing custom instructions remain in Chat; copy any rules you also want in Work into its box. Work rules apply on launch and subsequent follow-ups/resumes, including changes and clearing. Saving does not interrupt an active turn. About you and Response style are shared; conversation instructions take priority. These explicit settings work independently of Memory learning. Temporary chats exclude them unless personalization is enabled. Guide: /docs/custom-instructions."
      ]
    },
    {
      "guideType": "docs",
      "slug": "work-prompts",
      "title": "Writing a good Work task",
      "description": "Specify the outcome, references, constraints, review points, and definition of done.",
      "navIcon": "list-check",
      "navSectionSlug": "work-agent",
      "navOrder": 21,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My brief names an output format and audience.",
          "My sources and scope are explicit.",
          "I included a review point and a concrete acceptance check."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Make success observable",
            "copy": "A good Work brief describes the result someone will use. Include the audience, exact output formats, source material, limits, and a test that proves the work is ready. State assumptions Work may make and which choices need your input."
          },
          {
            "type": "work-prompt",
            "title": "A reusable Work brief",
            "code": "Outcome: Create [deliverable] for [audience].\nContext: Use [attached files or facts]. Preserve [details].\nFormat: Deliver [editable artifact] plus [export format].\nScope: Include [must-haves]. Keep this first version to [limit].\nReview: Pause after [storyboard, outline, or prototype] so I can review it.\nDone means: Check [specific tests] and explain anything incomplete."
          },
          {
            "type": "section",
            "title": "1. Attach and explain references",
            "copy": "Upload the relevant source and identify its role: “Use this as factual source material” or “Match this visual style.” State which file is authoritative if several disagree. Work only has the context available in the task; do not assume it can see a file on your device that you have not attached."
          },
          {
            "type": "section",
            "title": "2. Choose an appropriate review point",
            "copy": "For a short document, ask for a complete first draft. For a video, approve the storyboard and references before generating every clip. A requested pause is an instruction to Work, so check that it has reached the review point before approving the next phase."
          },
          {
            "type": "section",
            "title": "3. Define checks and delivery",
            "copy": "For a game, list controls, collisions and restart. For a budget, give a known total. For a deck, ask it to inspect every slide. Request the actual files and an explanation of what was verified, rather than treating a confident completion message as proof."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/briefing-agents"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Writing a good Work task | Gab AI Docs",
        "description": "Specify the outcome, references, constraints, review points, and definition of done. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, writing a good work task"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-prompts",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "writing a good work task"
      ],
      "navigation": {
        "previous": {
          "label": "Work overview",
          "href": "/docs/work"
        },
        "next": {
          "label": "Make a video, step by step",
          "href": "/docs/work-videos"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-artifacts",
      "title": "Artifact editors and exports",
      "description": "Know which artifact to request, what you can edit, and how to take your work with you.",
      "navIcon": "layer-group",
      "navSectionSlug": "work-agent",
      "navOrder": 24,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I can name the artifact and export format for my project.",
          "I know when Add to chat still requires Send.",
          "I understand that a revised project needs a fresh export."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Choose the result before choosing the tool",
            "copy": "Tell Work what you want to make. It selects tools while you describe the result and review it. Ask for the editable artifact as well as any final download you need. An answer pasted into chat, an editable project, and an exported file serve different purposes."
          },
          {
            "type": "unordered-list",
            "title": "What each artifact is for",
            "items": [
              "Document: edit text, headings and tables, use AI rewrite, then export Word or PDF.",
              "Spreadsheet: edit cells, formulas, formatting and sheets, then export Excel.",
              "Presentation: edit slides and speaker notes, rehearse with Present, then export PowerPoint or PDF.",
              "Image: inspect versions, select an area, request edits or reframing, then download the chosen image.",
              "Code and HTML: run supported HTML in Preview, inspect Code (or Source for Markdown) and available Changes, and point to revisions with Select or Highlight. Source is a viewer; ask Work for code changes.",
              "Video project: review reference media and clips, compare takes, and create a final video export."
            ]
          },
          {
            "type": "section",
            "title": "1. Open the delivered file",
            "copy": "Choose the artifact card in the Work conversation. A supported HTML file opens a preview, while other code types show source. A PDF is a finished document preview; ask for an editable document when you need to change its content inside Gab AI."
          },
          {
            "type": "section",
            "title": "2. Use the right kind of edit",
            "copy": "Text, cells and slides support direct editing in their respective editors. In the image studio and HTML preview, Add to chat prepares an AI revision: you still review and send it in chat. Saving a direct edit and asking Work to generate a new result are different actions."
          },
          {
            "type": "section",
            "title": "3. Keep the deliverable current",
            "copy": "Wait for saves to finish, compare the revised artifact with the original, and export again after changes. For a multi-file project, ask Work for all required assets and instructions. Check the downloaded file before sending it to someone else."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/agent-tools"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          },
          {
            "type": "section",
            "title": "Share a public site",
            "copy": "For websites, browser games, and interactive HTML, choose Publish site on the Work creation to get a public link for friends. Review the address, public access, and plan limits, then wait for confirmed publication before copying the link. Manage expiration and unpublishing in My sites. On older clients, ask Work to publish the creation in the same chat."
          },
          {
            "type": "quick-links",
            "title": "Publishing guide",
            "items": [
              {
                "label": "Publish and share your sites",
                "href": "/docs/sites"
              }
            ]
          }
        ],
        "title": "Artifact editors and exports | Gab AI Docs",
        "description": "Know which artifact to request, what you can edit, and how to take your work with you. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, artifact editors and exports"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-artifacts",
      "updatedAt": "2026-09-20T00:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "artifact editors and exports"
      ],
      "navigation": {
        "previous": {
          "label": "Files, code & games",
          "href": "/docs/work-files"
        },
        "next": {
          "label": "Build a playable video game",
          "href": "/docs/work-games"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z",
      "supportNotes": [
        "Documents support text, headings and tables, AI revisions and Word/PDF export; spreadsheets support cells, formulas, formatting and sheets with Excel export; presentations support slides, speaker notes, Present and PowerPoint/PDF export. A PDF preview is a finished-file view: request an editable document for in-app content edits. Controls and formats depend on the artifact and device.",
        "Image results can be revised, reframed or edited in a selected area where offered, then downloaded. Video projects can combine reference media, shots/takes, voiceover, music and final exports. Code/HTML results offer supported Preview, source and available Changes; source viewing is not a full code editor. Ask Work for source changes, or use Select/Highlight/Add to chat where offered to prepare a revision and review before sending.",
        "Open the delivered file card or Files library, review the content and use the available editor/export controls. Export again after edits and inspect the actual downloaded file. Saving/exporting a Mac artifact creates a copy; it does not change a connected source folder unless Work explicitly writes there. A preview or generated draft is not evidence of universal visual/functional validation, publication or successful external delivery."
      ]
    },
    {
      "guideType": "docs",
      "slug": "work-games",
      "title": "Build a playable video game",
      "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game.",
      "navIcon": "gamepad",
      "navSectionSlug": "work-agent",
      "navOrder": 25,
      "guideSeo": {
        "readMinutes": 18,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I can move, collect stars, win or lose, and restart without reloading.",
          "The controls fit on a small screen and do not cover the game.",
          "I reviewed a revision and tested the downloaded HTML."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A playable HTML game",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 18,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Start with one complete game loop",
            "copy": "Open Gab AI, select Work, and paste the starter prompt below. Keep the first version small: movement, a goal, obstacles, and restart. Work will create the files and check the game. Answer any question about the controls or scope in the same task."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Build Star Collector, a small playable browser game in one self-contained HTML file with embedded CSS and JavaScript. Move with arrow keys or WASD and on-screen touch controls. Collect 10 stars, avoid three moving obstacles, show score and a win or lose screen, and include a working Restart button. Use a midnight-blue space theme. No external assets, logins, or network calls. Test movement, collisions, scoring, restart, and a narrow mobile layout. Deliver the HTML game as a file artifact and tell me what you tested."
          },
          {
            "type": "section",
            "title": "2. Open the code artifact and play",
            "copy": "When Work delivers the HTML file, open its file card. Preview runs the game; click inside it before using keyboard controls. Collect stars, hit an obstacle, reach the win screen, and restart. Use the preview device selector to check a phone-sized layout. Test touch controls on a real touch device too."
          },
          {
            "type": "section",
            "title": "3. Point to a change",
            "copy": "In the HTML preview, use Select to pick a page element or Highlight to mark a region. Choose Ask for changes and describe the revision. A game drawn on a canvas is usually one element: highlight the region and describe the gameplay issue in words. Add to chat prepares the request; review it and press Send in the conversation."
          },
          {
            "type": "section",
            "title": "4. Inspect the revision",
            "copy": "Open the updated artifact. The Code tab lets you read the HTML file; Changes appears when an earlier source is available. For a simple static text element, Select may offer Edit text and Save copy. Use Work for game logic and dynamic text. Replay your test sequence after every change."
          },
          {
            "type": "section",
            "title": "5. Download and test the file",
            "copy": "Download the final HTML and open it in your browser. Because this exercise embeds its assets, it should run without a server. Check the downloaded copy as well as the preview. If you later request separate assets, ask Work to package the complete folder with instructions."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In Star Collector, make the player 20% slower and enlarge the touch controls. Keep the scoring, star count, visual style, and restart behavior. Update the existing game artifact and retest keyboard movement, collisions, winning, and restart."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If the preview is blank, try Restart preview, then tell Work what you see and ask it to fix the HTML artifact. If controls do nothing, click into Preview and leave Select or Highlight mode. If only the download fails, report that separately from the preview."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-games"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          },
          {
            "type": "section",
            "title": "Share a public site",
            "copy": "For websites, browser games, and interactive HTML, choose Publish site on the Work creation to get a public link for friends. Review the address, public access, and plan limits, then wait for confirmed publication before copying the link. Manage expiration and unpublishing in My sites. On older clients, ask Work to publish the creation in the same chat."
          },
          {
            "type": "quick-links",
            "title": "Publishing guide",
            "items": [
              {
                "label": "Publish and share your sites",
                "href": "/docs/sites"
              }
            ]
          }
        ],
        "title": "Build a playable video game | Gab AI Docs",
        "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, build a playable video game"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-games",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a playable video game"
      ],
      "navigation": {
        "previous": {
          "label": "Artifact editors and exports",
          "href": "/docs/work-artifacts"
        },
        "next": {
          "label": "Create and edit an image",
          "href": "/docs/work-images"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-images",
      "title": "Create and edit an image",
      "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format.",
      "navIcon": "image",
      "navSectionSlug": "work-agent",
      "navOrder": 26,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I inspected the image at a useful zoom.",
          "I sent one focused edit from Add to chat and compared the result.",
          "I downloaded the intended version and checked its dimensions."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An image and a revised version",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Give Work an image brief",
            "copy": "Select Work and send the prompt below. Name the subject, style, shape, and where the image will be used. You can attach a reference image and explain which details to preserve. For this exercise, keep the cover free of lettering so you can judge the composition first."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create a square cover image for a browser game called Star Collector: a friendly small astronaut reaching for glowing gold stars, midnight-blue space, simple bold shapes, playful illustrated style. Leave clear space at the top for a title I will add later. Do not generate lettering. Deliver the image as an artifact."
          },
          {
            "type": "section",
            "title": "2. Open the image studio",
            "copy": "Open the delivered image artifact. The image history lets you move between available images. Use Zoom to inspect the face, edges, hands, and background before making changes. You can also upload a source image into the studio."
          },
          {
            "type": "section",
            "title": "3. Edit a specific area",
            "copy": "Choose Edit for a general change, or Comment to select an area and describe what should change there. Drag a rectangle around the part of the image you want to revise. Write one clear instruction, such as “Replace the red planet in this area with a small gold star; preserve the astronaut.”"
          },
          {
            "type": "section",
            "title": "4. Send the edit and compare",
            "copy": "Press Add to chat. This prepares your instructions and image context in the conversation; review them and press Send. Wait for the new image, then compare it with the original in the image history. Selecting a tool alone does not run an edit."
          },
          {
            "type": "section",
            "title": "5. Try another tool and download",
            "copy": "Remove targets an unwanted object; Remove BG requests a transparent PNG; Resize reframes to an aspect ratio; Variation makes a new take. Try Resize at 16:9 for a banner and ask to preserve the astronaut. Download the version you prefer from the studio."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Reframe this Star Collector cover as a 16:9 banner. Keep the astronaut and illustrated style, move the subject to the right third, and leave uncluttered dark space on the left. Do not add text."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an edit changes too much, select the original in history and request a smaller change. AI reframing can redraw details; it is not a mechanical crop. For a transparent export, inspect the downloaded PNG in a viewer that shows transparency."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-images"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Create and edit an image | Gab AI Docs",
        "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, create and edit an image"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-images",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create and edit an image"
      ],
      "navigation": {
        "previous": {
          "label": "Build a playable video game",
          "href": "/docs/work-games"
        },
        "next": {
          "label": "Make an editable document",
          "href": "/docs/work-documents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-documents",
      "title": "Make an editable document",
      "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports.",
      "navIcon": "file-lines",
      "navSectionSlug": "work-agent",
      "navOrder": 27,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The facts and controls match the project.",
          "A direct edit reached the saved state.",
          "I opened the exported DOCX or PDF and checked its layout."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable guide, DOCX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Ask for the file you need",
            "copy": "Select Work and send the prompt below. Specify an editable document, audience, length, sections, and source material. If you have an existing brief, attach it and explain which facts must stay exact. This exercise uses a fictional game, so no research is needed."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable two-page player guide for a fictional browser game called Star Collector. Audience: first-time players age 10 and up. Include a title, short introduction, keyboard and touch controls, the goal of collecting 10 stars, three practical tips, and a troubleshooting table. Use clear headings and short paragraphs. Treat the game details in this brief as the source of truth. Deliver an editable document artifact, not just text in chat."
          },
          {
            "type": "section",
            "title": "2. Open and review the document",
            "copy": "Open the document artifact. Read the headings and controls before adjusting the appearance. Correct any mismatch with your game. A downloadable DOCX or PDF alone is not the same as an editable document artifact: ask Work for the editable version if you need in-app editing."
          },
          {
            "type": "section",
            "title": "3. Make a direct edit",
            "copy": "Click in the document and change the title or a sentence. Select text to use formatting controls, add a list, or insert a table. Use Find and replace for repeated wording. Watch the save status and wait until your changes are saved."
          },
          {
            "type": "section",
            "title": "4. Revise with AI",
            "copy": "Select a paragraph and use AI rewrite for a local wording change, or ask Work in the same task to revise a named section. Be explicit about the document and preserve the facts you have already checked. If the editor reports a newer version while you have local edits, review the choice before replacing your draft."
          },
          {
            "type": "section",
            "title": "5. Export and inspect",
            "copy": "Open Export and choose Word or PDF. Use Page setup to adjust print layout when needed. Open the downloaded file and inspect page breaks, headings, tables, and spacing. Keep the editable document for future revisions; download a fresh export after editing."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In the Star Collector player guide, rewrite only the introduction in a warmer voice and shorten it to 60 words. Preserve the controls, game rules, troubleshooting table, and existing headings. Update the editable document."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an export has an awkward page break, adjust Page setup or shorten that section and export again. If saving fails, keep the editor open and resolve the save error before closing. Use Version history to inspect available saved versions."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-documents"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Make an editable document | Gab AI Docs",
        "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, make an editable document"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-documents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make an editable document"
      ],
      "navigation": {
        "previous": {
          "label": "Create and edit an image",
          "href": "/docs/work-images"
        },
        "next": {
          "label": "Build a working spreadsheet",
          "href": "/docs/work-spreadsheets"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-spreadsheets",
      "title": "Build a working spreadsheet",
      "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook.",
      "navIcon": "table",
      "navSectionSlug": "work-agent",
      "navOrder": 28,
      "guideSeo": {
        "readMinutes": 14,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The initial grand total is 370.",
          "Changing Testing quantity to 5 updates the total to 395.",
          "The exported XLSX preserves the expected sheets and calculations."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable workbook and XLSX",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 14,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Describe inputs and calculations",
            "copy": "Use Work and send the prompt below. Name each column and the calculations that must use formulas. Request an editable workbook explicitly. The sample inputs are deliberately small so you can verify the answer yourself."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable spreadsheet artifact for a fictional Star Collector launch budget. Sheet Budget: columns Item, Quantity, Unit cost, Total. Rows: Art, 2, 75; Music, 1, 120; Testing, 4, 25. Use formulas for each row total and a grand total, currency formatting, and clearly marked input cells. Add a Summary sheet with the overall budget and a simple chart. Explain which cells I can change. Deliver an editable workbook and an XLSX export."
          },
          {
            "type": "section",
            "title": "2. Open the spreadsheet artifact",
            "copy": "Open the workbook and switch to Budget. Check headers, rows, number formats, and sheet names. Select a calculated cell and inspect its formula in the formula bar. The expected row totals are 150, 120, and 100; the starting grand total is 370."
          },
          {
            "type": "section",
            "title": "3. Test a formula by changing an input",
            "copy": "Change Testing quantity from 4 to 5. Its total should become 125 and the grand total should become 395. Check that Summary agrees. A total that does not change may be hard-coded or reference the wrong cell; ask Work to repair the formula."
          },
          {
            "type": "section",
            "title": "4. Refine the workbook",
            "copy": "Use the editor to adjust cell formatting, add a sheet, or rename a tab. Ask Work to add a contingency calculation in the same workbook. State which inputs and formulas must remain intact, then repeat the known-answer test."
          },
          {
            "type": "section",
            "title": "5. Export and reopen",
            "copy": "Wait for the saved state, then use Export for an Excel workbook. Open the XLSX in your spreadsheet application and check the formulas and chart again. CSV is useful for a flat table, but does not preserve workbook sheets, formatting, or live formulas."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Update this launch budget workbook with a clearly labeled 10% contingency based on the grand total and a total including contingency. Preserve the input rows and existing formulas. With a base total of 395, the contingency should be 39.50 and the final amount 434.50."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a formula is unsupported in the editor, ask Work for a compatible formula or a simpler calculation and verify the export separately. Never judge a spreadsheet only by how polished it looks; change an input and check the result."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-spreadsheets"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Build a working spreadsheet | Gab AI Docs",
        "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, build a working spreadsheet"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-spreadsheets",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a working spreadsheet"
      ],
      "navigation": {
        "previous": {
          "label": "Make an editable document",
          "href": "/docs/work-documents"
        },
        "next": {
          "label": "Create a presentation",
          "href": "/docs/work-presentations"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-presentations",
      "title": "Create a presentation",
      "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export.",
      "navIcon": "chalkboard",
      "navSectionSlug": "work-agent",
      "navOrder": 29,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "Each slide communicates one clear idea.",
          "I edited a slide and reviewed the speaker notes.",
          "I presented the deck and inspected the exported PPTX or PDF."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable deck, PPTX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Brief Work on the story",
            "copy": "Send the starter prompt in Work. Give the audience, purpose, slide count, visual style, and facts. Attach your game cover if you want Work to use it, and identify it as the title-slide image. Ask for an editable presentation artifact."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable five-slide presentation for the fictional Star Collector browser game. Slides: title and one-sentence pitch; how to play; what makes it fun; a simple launch plan; next steps. Audience: friends trying the prototype. Use midnight blue, white, and gold, one clear idea per slide, large readable text, and short speaker notes. Do not invent user numbers or market statistics. Deliver an editable presentation artifact."
          },
          {
            "type": "section",
            "title": "2. Review the slide sequence",
            "copy": "Open the deck artifact and use the slide thumbnails to inspect every slide. Check the narrative before polishing: a viewer should understand the game, its controls, and the next step. Remove unsupported claims or ask Work to replace them with clearly labeled plans."
          },
          {
            "type": "section",
            "title": "3. Edit a slide and its notes",
            "copy": "Select a slide and edit its text in the presentation editor. Shorten a crowded title, adjust the content, and update Speaker notes. Check the save status. Keep explanations in the notes when the slide itself would become too dense."
          },
          {
            "type": "section",
            "title": "4. Rehearse and revise",
            "copy": "Use Present to walk through the deck. Read titles from a distance and check that images are not stretched or text clipped. Ask Work to revise one named slide while preserving the rest. Inspect the revised slide and any surrounding slides it affects."
          },
          {
            "type": "section",
            "title": "5. Export for your audience",
            "copy": "Use Export to download PowerPoint for further editing or PDF for fixed pages. Open the exported file and review all slides. Keep the editable artifact for future updates and re-export after changes."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Revise only slide 3 of the Star Collector presentation. Replace the long paragraph with three short points about simple controls, quick rounds, and replayability. Keep the midnight-blue and gold design and preserve the other four slides."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If text clips in an export, shorten it or request a layout adjustment. A PDF has fixed pages; use the PowerPoint export when your recipient needs to edit the slides."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-presentations"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Create a presentation | Gab AI Docs",
        "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, create a presentation"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-presentations",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create a presentation"
      ],
      "navigation": {
        "previous": {
          "label": "Build a working spreadsheet",
          "href": "/docs/work-spreadsheets"
        },
        "next": {
          "label": "Mac app",
          "href": "/docs/mac-app"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-videos",
      "title": "Make a video, step by step",
      "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video.",
      "navIcon": "film",
      "navSectionSlug": "work-agent",
      "navOrder": 22,
      "guideSeo": {
        "readMinutes": 20,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I approved the storyboard and inspected the reference images.",
          "I watched every included clip and selected the intended takes.",
          "I watched the full export with sound and downloaded the correct MP4."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A reviewed video project and MP4",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 20,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/docs/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Plan before generating",
            "copy": "In Work, send the starter prompt below. Review the proposed shots, duration, aspect ratio, and sound. Start with three short shots so each is easy to inspect. Ask about the expected work and available credits before approving media generation; video generations and revisions use credits and timing can vary."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Help me make a 15-second, 16:9 teaser for a fictional space game called Star Collector. Three shots: a small astronaut notices a gold star; the astronaut dodges an asteroid; a field of stars reveals the adventure ahead. Playful illustrated style, midnight blue and gold, instrumental music, no voiceover or generated lettering. First propose a three-shot storyboard and the assets needed. Wait for my approval before generating media. We will review the reference images, then clips, then export the finished video."
          },
          {
            "type": "section",
            "title": "2. Create and review reference media",
            "copy": "Once the plan is right, tell Work to generate the reference images and pause for review. Open the video project artifact. Reference media groups available assets under Scenes, Characters, Other, and Audio. Select a scene image, inspect the subject and style, and use its available redo or image-choice controls to improve it before animating."
          },
          {
            "type": "section",
            "title": "3. Generate clips from the approved look",
            "copy": "Tell Work which reference images to use and ask it to generate the clips, pausing before the final export. Image choices may be locked while clips are generating. In Clips, select each shot from the timeline and play it. Check motion, continuity, duration, and whether the intended action actually happens."
          },
          {
            "type": "section",
            "title": "4. Improve one shot",
            "copy": "Use the selected shot’s Redo control to describe a specific change. Compare available takes and choose the one you want included. Use the timeline controls to reorder or exclude clips where available. Excluding every clip leaves nothing to export. Keep a shot you like instead of regenerating the whole film."
          },
          {
            "type": "section",
            "title": "5. Export the chosen sequence",
            "copy": "After reviewing all included clips and audio, use the project’s export action. Wait for stitching to finish; editing may be locked during that job. Open Final video and play the full result with sound. Check transitions, beginning and ending, and the music level before choosing Download video."
          },
          {
            "type": "section",
            "title": "6. Make a new version",
            "copy": "For a finished video, use Make changes to describe the next revision. Exports keeps earlier finished versions available. If you change clip choices or order, use Create new export to build an updated film. The previous MP4 does not automatically change when you edit the project."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Redo only shot 2 of the Star Collector teaser. Make the asteroid pass slowly behind the astronaut, with a steady camera and the same character design and blue-and-gold palette. Preserve shots 1 and 3 and the music. Let me compare the revised clip before creating a new export."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a clip fails, retry that shot rather than the whole project. If an export is queued, wait for the active job before requesting another. If no export arrives, use the displayed retry action and report the error in the task. A downloaded clip is only one shot; download from Final video for the complete film."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/docs/learn/work-videos"
              },
              {
                "label": "Explore all Work projects",
                "href": "/docs#learn"
              }
            ]
          }
        ],
        "title": "Make a video, step by step | Gab AI Docs",
        "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, make a video, step by step"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-videos",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make a video, step by step"
      ],
      "navigation": {
        "previous": {
          "label": "Writing a good Work task",
          "href": "/docs/work-prompts"
        },
        "next": {
          "label": "Files, code & games",
          "href": "/docs/work-files"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-files",
      "title": "Files, code & games",
      "description": "Choose a file format and follow a complete Work tutorial, from first brief to final export.",
      "navIcon": "file",
      "navSectionSlug": "work-agent",
      "navOrder": 23,
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Work"
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Ask Work for the artifact and the export",
            "copy": "Start with the editable result you want, then name the file format you need to deliver. The tutorials below cover creating the artifact, making a change, and checking the downloaded file."
          },
          {
            "type": "quick-links",
            "title": "Choose a walkthrough",
            "items": [
              {
                "label": "Build a playable video game",
                "href": "/docs/work-games"
              },
              {
                "label": "Create and edit an image",
                "href": "/docs/work-images"
              },
              {
                "label": "Make an editable document",
                "href": "/docs/work-documents"
              },
              {
                "label": "Build a working spreadsheet",
                "href": "/docs/work-spreadsheets"
              },
              {
                "label": "Create a presentation",
                "href": "/docs/work-presentations"
              },
              {
                "label": "Make a video, step by step",
                "href": "/docs/work-videos"
              }
            ]
          },
          {
            "type": "section",
            "title": "Check the final file",
            "copy": "Opening a preview is only part of review. Download the deliverable and open it in its intended app. For multiple files, ask Work for a complete package with assets and instructions. For another round of edits, return to the same Work task and identify the artifact."
          },
          {
            "type": "section",
            "title": "Share a public site",
            "copy": "For websites, browser games, and interactive HTML, choose Publish site on the Work creation to get a public link for friends. Review the address, public access, and plan limits, then wait for confirmed publication before copying the link. Manage expiration and unpublishing in My sites. On older clients, ask Work to publish the creation in the same chat."
          },
          {
            "type": "quick-links",
            "title": "Publishing guide",
            "items": [
              {
                "label": "Publish and share your sites",
                "href": "/docs/sites"
              }
            ]
          }
        ],
        "title": "Files, code & games | Gab AI Docs",
        "description": "Choose a file format and follow a complete Work tutorial, from first brief to final export. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, files, code & games"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-files",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "files, code & games"
      ],
      "navigation": {
        "previous": {
          "label": "Make a video, step by step",
          "href": "/docs/work-videos"
        },
        "next": {
          "label": "Artifact editors and exports",
          "href": "/docs/work-artifacts"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "sites",
      "title": "Publish and share your sites",
      "description": "Turn a Work website, HTML page, or browser game into a public link for friends. Publish, share, update, and manage it in My sites.",
      "navIcon": "globe",
      "navSectionSlug": "work-agent",
      "navOrder": 65,
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Work"
      },
      "meta": {
        "title": "Publish Websites and Games | Gab AI Docs",
        "description": "Publish websites and games made in Chat or Work, share a public link, and manage expiration and unpublishing in My sites.",
        "keywords": "Gab AI, My sites, publish site, share game, HTML, public link",
        "pageCopy": [
          {
            "type": "section",
            "title": "From a creation to a public link",
            "copy": "My sites lets you share websites, browser games, and interactive HTML pages made in Chat or Work. Friends open the published link in a browser without a Gab AI account. Previewing or downloading a creation does not publish it. You choose when to make its files public."
          },
          {
            "type": "section",
            "title": "1. Build and test your creation",
            "copy": "Open the chat where you made the page or game. Try the preview, including controls, links, images, and a phone-sized layout. Ask Work to fix anything that does not work before publishing. Sites hosts static HTML, CSS, JavaScript, and related assets; a project that needs a server, database, or private API key needs a different hosting setup or a static version."
          },
          {
            "type": "section",
            "title": "2. Choose Publish site",
            "copy": "Choose Publish site beneath the HTML or game preview in Chat or Work, or open its saved HTML file and publish from the viewer. Inline HTML is saved automatically when you publish. The panel explains what becomes public and shows your current plan limits."
          },
          {
            "type": "section",
            "title": "3. Pick a site address",
            "copy": "Enter a name with 3–40 lowercase letters, numbers, and single hyphens, starting and ending with a letter or number. Include at least one letter. Your address will look like star-collector.sites.gabusercontent.com. The panel checks availability and naming rules as you type. If the name is taken or not allowed, choose a suggestion or enter another. Replacing a site you own requires an explicit confirmation. Reserved names and names that impersonate brands or sign-in pages are not allowed."
          },
          {
            "type": "section",
            "title": "4. Publish and wait for confirmation",
            "copy": "Review the public-access explanation, then choose Publish site in the panel. This publishes the saved HTML and its referenced assets directly, without starting another agent turn or using extra Work credits. The panel shows progress and then the shareable link. It does not publish the whole workspace or private conversation. If an asset is missing, ask the chat to include it in the HTML or generate the files together, then retry. Wait for Ready to share before copying the link."
          },
          {
            "type": "section",
            "title": "5. Share with friends",
            "copy": "Choose Copy link on the published-site card and send it to friends. Open the public link yourself and check the page or game outside the private preview before sharing. Anyone with the link can view the published files, so remove private information, credentials, and secrets before publishing."
          },
          {
            "type": "section",
            "title": "Manage links in My sites",
            "copy": "Open My sites from the app navigation or the publishing panel to see your sites, public links, status, and time remaining. Use Open or Copy link to share an online site. Unpublish asks for confirmation, takes the site offline, and deletes its hosted files; copies already saved by visitors cannot be recalled. Keep a downloaded copy or the original Work files. Ask Work to update an existing site by its exact link, or to publish again after expiry. Editing the private creation does not automatically update its public site."
          },
          {
            "type": "section",
            "title": "Plan limits and expiration",
            "copy": "Publishing requires an eligible plan with Work access. The publishing panel and My sites show the current online duration and maximum live sites for your account. Sites expire; this is not permanent hosting. If your live-site limit is reached, unpublish an old site before publishing another. A supported site can contain up to 50 MB of files; ask Work to reduce large media if it is too large. Do not assume republishing, renewal, or keeping a name is guaranteed after expiration."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "Publishing errors appear in the panel. Resolve a name conflict, plan limit, missing asset, or hosting configuration issue before retrying. If the connection drops, check My sites for a completed publication before trying again. If a link stops working, check My sites for expiration, unpublishing, or removal. If images or game assets are missing, ask Work to publish the required assets together and verify relative paths. Do not share the private preview or download URL as a substitute for a published site."
          },
          {
            "type": "quick-links",
            "title": "Keep creating",
            "items": [
              {
                "label": "Build a playable game",
                "href": "/docs/learn/work-games"
              },
              {
                "label": "Artifact editors and exports",
                "href": "/docs/work-artifacts"
              },
              {
                "label": "Work overview",
                "href": "/docs/work"
              }
            ]
          }
        ]
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#040404",
      "canonicalPath": "/docs/sites",
      "updatedAt": "2026-09-28T16:00:00.000Z",
      "publishedAt": "2026-09-28T16:00:00.000Z",
      "searchKeywords": [
        "sites",
        "my sites",
        "publish",
        "share",
        "public link",
        "hosting",
        "game",
        "html",
        "expiration",
        "unpublish"
      ],
      "supportNotes": [
        "My sites publishes static websites, browser games and interactive HTML from Work at https://<name>.sites.gabusercontent.com/. Friends need no Gab AI account to open a live site. A private artifact preview, a download and a public site are different; do not claim previewing or downloading publishes anything.",
        "On compatible clients, choose Publish site beneath the HTML/game preview or in its viewer opened from the Work card, choose the address, review public access and plan limits, then choose Publish site. This requests publishing in the same Work task and uses Work credits. Let the current run finish first. On older clients ask Work to publish the creation in the same chat. Wait for Ready to share or a verified site-published result before sharing; an accepted request is not publication.",
        "Site names are 3–40 lowercase letters, numbers and single hyphens, start/end alphanumeric and include a letter. Availability, reserved-name and content rules are checked by the publisher. If a requested name is unavailable or disallowed, ask for another; never claim a proposed URL is live or silently replace an existing site.",
        "Choose Copy link on the published card or open My sites to Open, Copy link or Unpublish. Anyone with the link can view the site files. Only the selected creation and required assets should be published, never secrets, unrelated files or the private chat. Unpublish requires confirmation, stops the link and deletes hosted files; it cannot recall visitors’ saved copies.",
        "Sites expire according to the account’s eligible plan; show the current TTL and live-site cap from My sites/publishing UI, not an invented permanent-hosting promise. Unpublish an old site at the live-site limit. Ask Work to update or republish using the exact site link; private edits do not update a site automatically. Keep the original/downloaded files. Static sites have a 50 MB total limit; servers, databases and secret keys need another setup or a static adaptation.",
        "Questions such as “How do I publish the game I made here?”, “Where is My sites?” and “Why did my published site expire?” are product support; explain the steps without launching paid work. “Publish this game now” is an action request for Work, not just help. Do not confuse third-party website hosting questions with Gab AI support."
      ]
    }
  ]
}
